import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Play, Pause, Square, SkipBack, SkipForward,
  Mic, MicOff, Volume2, VolumeX, Download, Upload, Send,
  Clock, DollarSign, CheckCircle, XCircle, AlertCircle,
  FileAudio, User, Mail, Music, Calendar, Edit, Trash2,
  Eye, MessageSquare, RefreshCw, Headphones, Radio, Waveform,
  Settings, Layers, ZoomIn, ZoomOut, Maximize2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminRecordingStudio = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'studio'
  const [showPricing, setShowPricing] = useState(true);
  
  // Studio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [zoom, setZoom] = useState(1);
  const audioRef = useRef(null);
  const waveformRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    fetchStats();
    fetchTiers();
  }, [statusFilter]);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/api/recording-studio/tiers`);
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API}/api/recording-studio/projects`
        : `${API}/api/recording-studio/projects?status=${statusFilter}`;
      const res = await fetch(url);
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/recording-studio/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const updateProjectStatus = async (projectId, status) => {
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      if (!res.ok) throw new Error('Failed to update');
      
      toast.success(`Project ${status === 'completed' ? 'completed' : 'updated'}`);
      fetchProjects();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update project status');
    }
  };

  const uploadRecordedTrack = async (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('track_name', file.name);
    
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${projectId}/upload-recorded`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      toast.success('Track uploaded successfully');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to upload track');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      review: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      revision: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Pro Tools Style Header */}
      <div className="bg-[#1a1a1a] border-b border-[#333]">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Radio className="w-6 h-6 text-red-500" />
              <h1 className="text-lg font-bold">Recording Studio</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Transport Controls */}
            <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-1">
              <button className="p-2 hover:bg-white/10 rounded">
                <SkipBack className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsPlaying(!isPlaying)}
                className={`p-2 rounded ${isPlaying ? 'bg-green-500 text-black' : 'hover:bg-white/10'}`}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button className="p-2 hover:bg-white/10 rounded">
                <Square className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsRecording(!isRecording)}
                className={`p-2 rounded ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'hover:bg-white/10'}`}
              >
                <div className={`w-3 h-3 rounded-full ${isRecording ? 'bg-white' : 'bg-red-500'}`} />
              </button>
              <button className="p-2 hover:bg-white/10 rounded">
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
            
            {/* Time Display */}
            <div className="bg-[#0a0a0a] px-4 py-2 rounded-lg font-mono text-lg">
              <span className="text-green-400">00:00:00</span>
              <span className="text-gray-500 mx-2">|</span>
              <span className="text-amber-400">120 BPM</span>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-1">
              <button 
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded text-sm ${viewMode === 'list' ? 'bg-amber-500 text-black' : 'hover:bg-white/10'}`}
              >
                Projects
              </button>
              <button 
                onClick={() => setViewMode('studio')}
                className={`px-3 py-1.5 rounded text-sm ${viewMode === 'studio' ? 'bg-amber-500 text-black' : 'hover:bg-white/10'}`}
              >
                Studio
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-60px)]">
        {/* Left Sidebar - Stats & Filters */}
        <div className="w-64 bg-[#141414] border-r border-[#333] p-4 flex flex-col">
          {/* Stats */}
          {stats && (
            <div className="space-y-3 mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overview</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-2xl font-bold text-amber-400">{stats.total_projects}</div>
                  <div className="text-xs text-gray-500">Total Projects</div>
                </div>
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
                  <div className="text-xs text-gray-500">Pending</div>
                </div>
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-400">{stats.in_progress}</div>
                  <div className="text-xs text-gray-500">In Progress</div>
                </div>
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-400">${stats.total_revenue}</div>
                  <div className="text-xs text-gray-500">Revenue</div>
                </div>
              </div>
            </div>
          )}

          {/* Status Filter */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Filter by Status</h3>
            <div className="space-y-1">
              {['all', 'pending', 'in_progress', 'review', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm capitalize transition-colors ${
                    statusFilter === status 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                      : 'hover:bg-white/5 text-gray-400'
                  }`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-auto">
            <button
              onClick={() => { fetchProjects(); fetchStats(); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#0a0a0a] hover:bg-white/10 rounded-lg text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {viewMode === 'list' ? (
            /* Project List View */
            <div className="p-6">
              {/* Pricing Tiers Section */}
              {showPricing && tiers.length > 0 && (
                <div className="mb-8 bg-gradient-to-br from-[#1a1a1a] to-[#141414] rounded-2xl border border-[#333] overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-[#333]">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      <h3 className="font-bold text-lg">Service Pricing</h3>
                    </div>
                    <button 
                      onClick={() => setShowPricing(false)}
                      className="text-gray-500 hover:text-white text-sm"
                    >
                      Hide
                    </button>
                  </div>
                  <div className="p-4 grid grid-cols-4 gap-4">
                    {tiers.map(tier => (
                      <div 
                        key={tier.id} 
                        className={`bg-[#0a0a0a] rounded-xl p-4 border ${
                          tier.id === 'demo' ? 'border-blue-500/30' :
                          tier.id === 'standard' ? 'border-purple-500/30' :
                          tier.id === 'premium' ? 'border-amber-500/30' :
                          'border-green-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${
                            tier.id === 'demo' ? 'text-blue-400' :
                            tier.id === 'standard' ? 'text-purple-400' :
                            tier.id === 'premium' ? 'text-amber-400' :
                            'text-green-400'
                          }`}>{tier.name}</span>
                          <span className="text-xs text-gray-500">
                            {tier.turnaround_days === 0 ? 'Live' : `${tier.turnaround_days}d`}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-white mb-1">
                          ${tier.price}
                          {tier.id === 'live_session' && <span className="text-sm text-gray-500">/hr</span>}
                        </div>
                        <p className="text-xs text-gray-500 mb-3">{tier.description}</p>
                        <ul className="space-y-1">
                          {tier.includes.slice(0, 3).map((item, i) => (
                            <li key={i} className="flex items-center gap-1 text-xs text-gray-400">
                              <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                              <span className="truncate">{item}</span>
                            </li>
                          ))}
                          {tier.includes.length > 3 && (
                            <li className="text-xs text-gray-600">+{tier.includes.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!showPricing && (
                <button 
                  onClick={() => setShowPricing(true)}
                  className="mb-4 text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
                >
                  <DollarSign className="w-4 h-4" />
                  Show Pricing Tiers
                </button>
              )}

              <div className="mb-6">
                <h2 className="text-2xl font-bold">Recording Projects</h2>
                <p className="text-gray-400">Manage client recording requests</p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-16">
                  <FileAudio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
                  <p className="text-gray-400">Projects will appear here when clients submit requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {projects.map(project => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#141414] border border-[#333] rounded-xl overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-semibold">{project.project_name}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {project.client_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {project.client_email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(project.created_at)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                              {project.status.replace('_', ' ')}
                            </span>
                            <span className="text-lg font-bold text-green-400">${project.price}</span>
                          </div>
                        </div>
                        
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Music className="w-4 h-4" />
                            {project.genre}
                          </span>
                          {project.tempo && (
                            <span className="flex items-center gap-1 text-gray-500">
                              <Clock className="w-4 h-4" />
                              {project.tempo} BPM
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-gray-500">
                            <FileAudio className="w-4 h-4" />
                            {project.tracks_uploaded?.length || 0} tracks uploaded
                          </span>
                          <span className="flex items-center gap-1 text-purple-400">
                            <Headphones className="w-4 h-4" />
                            {project.tracks_recorded?.length || 0} recorded
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions Bar */}
                      <div className="bg-[#0a0a0a] border-t border-[#333] px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedProject(project)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-sm hover:bg-amber-500/30 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Details
                          </button>
                          
                          {project.tracks_uploaded?.length > 0 && (
                            <a
                              href={`${API}${project.tracks_uploaded[0]?.url}`}
                              download
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Download Tracks
                            </a>
                          )}
                          
                          <label className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors cursor-pointer">
                            <Upload className="w-4 h-4" />
                            Upload Recording
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadRecordedTrack(project.id, e.target.files[0])}
                            />
                          </label>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {project.status === 'pending' && (
                            <button
                              onClick={() => updateProjectStatus(project.id, 'in_progress')}
                              className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
                            >
                              Start Working
                            </button>
                          )}
                          {project.status === 'in_progress' && (
                            <button
                              onClick={() => updateProjectStatus(project.id, 'review')}
                              className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors"
                            >
                              Send for Review
                            </button>
                          )}
                          {project.status === 'review' && (
                            <button
                              onClick={() => updateProjectStatus(project.id, 'completed')}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition-colors"
                            >
                              Mark Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Pro Tools Style Studio View */
            <div className="h-full flex flex-col bg-[#0a0a0a]">
              {/* Timeline Header */}
              <div className="h-8 bg-[#1a1a1a] border-b border-[#333] flex items-center px-4">
                <div className="w-48 shrink-0 border-r border-[#333] text-xs text-gray-500">TRACKS</div>
                <div className="flex-1 flex items-center">
                  {Array.from({ length: 32 }).map((_, i) => (
                    <div key={i} className="flex-1 text-center text-xs text-gray-600 border-l border-[#222]">
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tracks Area */}
              <div className="flex-1 overflow-auto">
                {/* Master Track */}
                <div className="flex border-b border-[#333]">
                  <div className="w-48 shrink-0 bg-[#1a1a1a] p-2 border-r border-[#333]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-amber-400">MASTER</span>
                      <div className="flex items-center gap-1">
                        <button className="p-1 hover:bg-white/10 rounded">
                          <Volume2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="h-2 bg-[#333] rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-gradient-to-r from-green-500 to-green-400 rounded-full" />
                    </div>
                  </div>
                  <div className="flex-1 h-16 bg-[#0f0f0f] relative">
                    <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-amber-500/20 to-transparent" />
                  </div>
                </div>

                {/* Client Tracks */}
                {selectedProject?.tracks_uploaded?.map((track, i) => (
                  <div key={i} className="flex border-b border-[#333]">
                    <div className="w-48 shrink-0 bg-[#1a1a1a] p-2 border-r border-[#333]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-blue-400 truncate">{track.filename}</span>
                        <div className="flex items-center gap-1">
                          <button className="p-1 hover:bg-white/10 rounded text-gray-400">
                            <Volume2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">Client Track {i + 1}</div>
                    </div>
                    <div className="flex-1 h-20 bg-[#0f0f0f] relative overflow-hidden">
                      {/* Waveform Placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-12 bg-blue-500/20 rounded flex items-center px-4">
                          <div className="flex items-end gap-px w-full h-full">
                            {Array.from({ length: 100 }).map((_, j) => (
                              <div
                                key={j}
                                className="flex-1 bg-blue-500/60"
                                style={{ height: `${20 + Math.random() * 60}%` }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Recorded Tracks */}
                {selectedProject?.tracks_recorded?.map((track, i) => (
                  <div key={i} className="flex border-b border-[#333]">
                    <div className="w-48 shrink-0 bg-[#1a1a1a] p-2 border-r border-[#333]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-purple-400 truncate">{track.track_name}</span>
                        <div className="flex items-center gap-1">
                          <button className="p-1 hover:bg-white/10 rounded text-gray-400">
                            <Volume2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">Recorded {i + 1}</div>
                    </div>
                    <div className="flex-1 h-20 bg-[#0f0f0f] relative overflow-hidden">
                      {/* Waveform Placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-12 bg-purple-500/20 rounded flex items-center px-4">
                          <div className="flex items-end gap-px w-full h-full">
                            {Array.from({ length: 100 }).map((_, j) => (
                              <div
                                key={j}
                                className="flex-1 bg-purple-500/60"
                                style={{ height: `${20 + Math.random() * 60}%` }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Empty Recording Track */}
                <div className="flex border-b border-[#333]">
                  <div className="w-48 shrink-0 bg-[#1a1a1a] p-2 border-r border-[#333]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-red-400">REC</span>
                      <div className="flex items-center gap-1">
                        <button className={`p-1 rounded ${isRecording ? 'bg-red-500 text-white' : 'hover:bg-white/10 text-gray-400'}`}>
                          <Mic className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">Armed for recording</div>
                  </div>
                  <div className="flex-1 h-20 bg-[#0f0f0f] relative border-l-2 border-red-500/30">
                    {isRecording && (
                      <div className="absolute inset-0 bg-red-500/10 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Controls */}
              <div className="h-12 bg-[#1a1a1a] border-t border-[#333] flex items-center justify-between px-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <ZoomOut className="w-4 h-4 text-gray-500" />
                    <input
                      type="range"
                      min="0.5"
                      max="4"
                      step="0.1"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="w-24"
                    />
                    <ZoomIn className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
                
                <div className="text-sm text-gray-400">
                  {selectedProject ? (
                    <span>Editing: <span className="text-amber-400">{selectedProject.project_name}</span></span>
                  ) : (
                    <span>Select a project to view in studio mode</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-white/10 rounded">
                    <Settings className="w-4 h-4 text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-white/10 rounded">
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Project Details */}
        <AnimatePresence>
          {selectedProject && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-[#141414] border-l border-[#333] overflow-hidden"
            >
              <div className="p-4 w-80">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Project Details</h3>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <XCircle className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500">Project Name</label>
                    <p className="font-semibold">{selectedProject.project_name}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500">Client</label>
                    <p>{selectedProject.client_name}</p>
                    <p className="text-sm text-gray-400">{selectedProject.client_email}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500">Description</label>
                    <p className="text-sm text-gray-300">{selectedProject.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Genre</label>
                      <p>{selectedProject.genre}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Tempo</label>
                      <p>{selectedProject.tempo || '-'} BPM</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">Client Tracks</label>
                    <div className="space-y-2">
                      {selectedProject.tracks_uploaded?.map((track, i) => (
                        <a
                          key={i}
                          href={`${API}${track.url}`}
                          download
                          className="flex items-center gap-2 p-2 bg-[#0a0a0a] rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <FileAudio className="w-4 h-4 text-blue-400" />
                          <span className="text-sm truncate flex-1">{track.filename}</span>
                          <Download className="w-4 h-4 text-gray-500" />
                        </a>
                      ))}
                    </div>
                  </div>
                  
                  {selectedProject.tracks_recorded?.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-500 mb-2 block">Recorded Tracks</label>
                      <div className="space-y-2">
                        {selectedProject.tracks_recorded?.map((track, i) => (
                          <a
                            key={i}
                            href={`${API}${track.url}`}
                            download
                            className="flex items-center gap-2 p-2 bg-[#0a0a0a] rounded-lg hover:bg-white/5 transition-colors"
                          >
                            <Headphones className="w-4 h-4 text-purple-400" />
                            <span className="text-sm truncate flex-1">{track.track_name}</span>
                            <Download className="w-4 h-4 text-gray-500" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedProject.notes && (
                    <div>
                      <label className="text-xs text-gray-500">Client Notes</label>
                      <p className="text-sm text-gray-300 bg-[#0a0a0a] p-3 rounded-lg">{selectedProject.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminRecordingStudio;
